// The one file that maps server error codes to plain-English client copy (§27.1).
// UI code must never render error.message from the wire - only this map.
export const ERROR_CODES = [
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'ROLE_FORBIDDEN',
    'PLAN_EXPIRED',
    'UPGRADE_REQUIRED',
    'VALIDATION',
    'NOT_FOUND',
    'CONFLICT',
    'RATE_LIMITED',
    'INSUFFICIENT_CREDITS',
    'PAYMENTS_NOT_READY',
    'GONE',
    'RENDER_FAILED',
    'INTERNAL',
    'UNPROCESSABLE',
    'DOWNGRADE_BLOCKED',
];
export const errorCopy = {
    UNAUTHENTICATED: {
        title: 'Please sign in',
        body: 'Your session has ended. Sign in again to keep going.',
    },
    FORBIDDEN: {
        title: "You don't have access",
        body: "You don't have access to that - check you're in the right workspace.",
    },
    ROLE_FORBIDDEN: {
        title: "You don't have access",
        body: 'Your role doesn’t include this action. Ask an owner or manager if you need it.',
    },
    PLAN_EXPIRED: {
        title: 'Your trial has ended',
        body: 'Upgrade to keep editing - your data is safe and your customers can still pay deposits.',
        action: 'upgrade',
    },
    UPGRADE_REQUIRED: {
        title: 'Upgrade required',
        body: 'This feature is available on a higher plan.',
        action: 'upgrade',
    },
    VALIDATION: {
        title: "That doesn't look right",
        body: 'Double check the highlighted fields and try again.',
    },
    NOT_FOUND: {
        title: 'Not found',
        body: "We couldn't find that - it may have been moved or deleted.",
    },
    CONFLICT: {
        title: "That didn't go through",
        body: 'Something changed since you last loaded this. Refresh and try again.',
        action: 'retry',
    },
    RATE_LIMITED: {
        title: 'Slow down a little',
        body: "You're doing that a little too fast - give it a moment and try again.",
        action: 'wait',
    },
    INSUFFICIENT_CREDITS: {
        title: 'Out of AI credits',
        body: 'Buy more credits or upgrade your plan to keep generating mockups.',
        action: 'topup',
    },
    PAYMENTS_NOT_READY: {
        title: 'Online payments unavailable',
        body: 'This shop hasn’t finished connecting Stripe yet - contact them directly to pay.',
    },
    GONE: {
        title: 'This link is no longer valid',
        body: 'This quote link has expired or been revoked. Contact the shop for an updated one.',
    },
    RENDER_FAILED: {
        title: 'Mockup generation failed',
        body: "The AI render didn't come through - your credits were refunded. Try again in a moment.",
        action: 'retry',
    },
    INTERNAL: {
        title: 'Something went wrong on our end',
        body: 'Please try again - if it keeps happening, contact support.',
        action: 'retry',
    },
    UNPROCESSABLE: {
        title: "That doesn't work",
        body: 'Pick a date and time within the shop’s booking window and hours.',
    },
    DOWNGRADE_BLOCKED: {
        title: "You're over the new plan's limits",
        body: 'Bring your workspace within the target plan\'s limits and try again.',
    },
};
export const fallbackErrorCopy = {
    title: 'Something went wrong',
    body: 'Please try again.',
    action: 'retry',
};
export function copyForCode(code) {
    return errorCopy[code] ?? fallbackErrorCopy;
}
export class ApiError extends Error {
    code;
    requestId;
    status;
    issues;
    constructor(status, envelope) {
        super(envelope.error.message);
        this.name = 'ApiError';
        this.code = envelope.error.code;
        this.requestId = envelope.error.requestId;
        this.status = status;
        this.issues = envelope.error.issues;
    }
}
/**
 * Thrown by pure domain code that runs in *both* server and client (e.g. the
 * §29 pricing engine). Mirrors the server's `AppError` shape (which lives in
 * server/src/lib/errors.ts and cannot be imported by the shared package): an
 * internal `message` (server logs / Sentry only, never shown to a user) plus a
 * safe `userMessage`. Used for invariant violations (§29.8).
 */
export class AppError extends Error {
    code;
    userMessage;
    issues;
    constructor(code, internalMessage, options) {
        super(internalMessage, options?.cause ? { cause: options.cause } : undefined);
        this.name = 'AppError';
        this.code = code;
        // Internal messages are developer text; never let one reach a user.
        this.userMessage = options?.userMessage ?? errorCopy[code].body;
        this.issues = options?.issues;
    }
}
//# sourceMappingURL=errors.js.map